/**
 * AgentPipelinePanel — Admin Dashboard view:
 *   • Table: each agent's lead pipeline counts (Total / New / Contacted / LFB / Pick Pool / Booked)
 *   • Below: stacked bar chart showing status breakdown per agent
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { BarChart3, Loader2 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { API } from "../App";

const COLORS = {
  new: "#60a5fa",
  contacted: "#fbbf24",
  pick_pool: "#34d399",
  lfb: "#a78bfa",
  booked: "#f97316",
};

const AgentPipelinePanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/dashboard/pipeline-by-agent`);
      setData(res.data);
    } catch (_) { /* admin-only — silent for non-admins */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return null;
  if (!data || !data.agents) return null;

  const chartData = data.agents.map((a) => ({
    name: a.name?.split(" ")[0] || a.email,
    new: a.new,
    contacted: a.contacted,
    pick_pool: a.pick_pool,
    lfb: a.lfb,
    booked: a.booked,
  }));

  return (
    <Card className="border-slate-200" data-testid="agent-pipeline-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-indigo-600" />
          Agent-wise Leads in Pipeline
        </CardTitle>
      </CardHeader>
      <CardContent>
        {data.agents.length === 0 ? (
          <p className="text-center text-slate-400 text-sm py-6">No agents with assigned leads</p>
        ) : (
          <>
            <div className="overflow-x-auto mb-6">
              <table className="w-full text-sm" data-testid="agent-pipeline-table">
                <thead className="bg-slate-50 border-y border-slate-200">
                  <tr>
                    <th className="text-left text-xs font-semibold text-slate-600 uppercase py-2.5 px-4">Agent</th>
                    <th className="text-center text-xs font-semibold text-slate-600 uppercase py-2.5 px-4">Total</th>
                    <th className="text-center text-xs font-semibold uppercase py-2.5 px-4" style={{ color: COLORS.new }}>New</th>
                    <th className="text-center text-xs font-semibold uppercase py-2.5 px-4" style={{ color: COLORS.contacted }}>Contacted</th>
                    <th className="text-center text-xs font-semibold uppercase py-2.5 px-4" style={{ color: COLORS.pick_pool }}>Pick Pool</th>
                    <th className="text-center text-xs font-semibold uppercase py-2.5 px-4" style={{ color: COLORS.lfb }}>LFB</th>
                    <th className="text-center text-xs font-semibold uppercase py-2.5 px-4" style={{ color: COLORS.booked }}>Booked</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.agents.map((a) => (
                    <tr key={a.user_id} className="hover:bg-slate-50" data-testid={`pipeline-row-${a.user_id}`}>
                      <td className="py-2.5 px-4">
                        <div className="font-medium text-slate-900">{a.name}</div>
                        <div className="text-xs text-slate-500">{a.email}</div>
                      </td>
                      <td className="py-2.5 px-4 text-center font-bold text-slate-900 tabular-nums">{a.total}</td>
                      <td className="py-2.5 px-4 text-center tabular-nums">{a.new}</td>
                      <td className="py-2.5 px-4 text-center tabular-nums">{a.contacted}</td>
                      <td className="py-2.5 px-4 text-center tabular-nums">{a.pick_pool}</td>
                      <td className="py-2.5 px-4 text-center tabular-nums">{a.lfb}</td>
                      <td className="py-2.5 px-4 text-center tabular-nums">{a.booked}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="h-64" data-testid="agent-pipeline-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748b" }} />
                  <YAxis tick={{ fontSize: 12, fill: "#64748b" }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: "12px" }} />
                  <Bar dataKey="new" stackId="a" fill={COLORS.new} name="New" />
                  <Bar dataKey="contacted" stackId="a" fill={COLORS.contacted} name="Contacted" />
                  <Bar dataKey="pick_pool" stackId="a" fill={COLORS.pick_pool} name="Pick Pool" />
                  <Bar dataKey="lfb" stackId="a" fill={COLORS.lfb} name="LFB" />
                  <Bar dataKey="booked" stackId="a" fill={COLORS.booked} name="Booked" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default AgentPipelinePanel;
